import React, { useMemo, useState } from 'react';
import { MapPin, Navigation, Search } from 'lucide-react';
import ServiceStatusBadge, { getStatusLabel } from '../services/ServiceStatusBadge';
import { serviceStatusAllowsVendorTracking } from '../../utils/vendorTracking';
import { formatDateTime } from '../../utils/dateUtils';

function truncate(text, max = 28) {
    if (!text || text === 'N/A' || text === '—') return '—';
    return text.length > max ? `${text.slice(0, max)}…` : text;
}

const LiveJobsList = ({
    jobs,
    statusCatalog,
    selectedJobId,
    statusFilter,
    onStatusFilterChange,
    onSelectJob,
    onTrackJob,
}) => {
    const [search, setSearch] = useState('');

    const statusCounts = useMemo(() => {
        const counts = {};
        jobs.forEach((job) => {
            counts[job.status] = (counts[job.status] || 0) + 1;
        });
        return counts;
    }, [jobs]);

    const statusChips = useMemo(() => {
        return Object.entries(statusCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8)
            .map(([code, count]) => ({
                code,
                count,
                label: getStatusLabel(code, statusCatalog),
            }));
    }, [statusCounts, statusCatalog]);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return jobs
            .filter((job) => {
                if (statusFilter && job.status !== statusFilter) return false;
                if (!term) return true;
                const haystack = [
                    job.service_number,
                    job.customer_name,
                    job.vendor_name,
                    job.driver_name,
                    job.status,
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                return haystack.includes(term);
            })
            .sort((a, b) => {
                const rank = { breached: 0, at_risk: 1, ok: 2 };
                const ra = rank[a.sla_state] ?? 2;
                const rb = rank[b.sla_state] ?? 2;
                if (ra !== rb) return ra - rb;
                return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
            });
    }, [jobs, search, statusFilter]);

    return (
        <div className="flex flex-col h-full">
            <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by service #, customer, vendor, driver…"
                    className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
                />
            </div>

            {statusChips.length > 0 ? (
                <div className="flex flex-wrap gap-2 mb-3">
                    <button
                        type="button"
                        onClick={() => onStatusFilterChange?.('')}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                            !statusFilter
                                ? 'bg-slate-800 text-white border-slate-800'
                                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        All ({jobs.length})
                    </button>
                    {statusChips.map((chip) => (
                        <button
                            key={chip.code}
                            type="button"
                            onClick={() =>
                                onStatusFilterChange?.(statusFilter === chip.code ? '' : chip.code)
                            }
                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                                statusFilter === chip.code
                                    ? 'bg-slate-800 text-white border-slate-800'
                                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                            }`}
                        >
                            {chip.label} ({chip.count})
                        </button>
                    ))}
                </div>
            ) : null}

            <div className="flex-1 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                <div className="overflow-auto max-h-[520px]">
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 border-b border-gray-200 sticky top-0 z-10">
                            <tr>
                                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    Service
                                </th>
                                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    Customer
                                </th>
                                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 hidden xl:table-cell">
                                    Vendor / Driver
                                </th>
                                <th className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    Status
                                </th>
                                <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 w-24">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-4 py-12 text-center">
                                        <p className="text-gray-500 font-medium">No jobs match your filters</p>
                                        <p className="text-xs text-gray-400 mt-1">Try clearing search or status filter</p>
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((job) => {
                                    const isSelected = selectedJobId === job.id;
                                    const trackable = serviceStatusAllowsVendorTracking(job.status);
                                    return (
                                        <tr
                                            key={job.id}
                                            onClick={() => onSelectJob?.(job)}
                                            className={`cursor-pointer transition-colors ${
                                                job.sla_state === 'breached'
                                                    ? 'bg-red-50 ring-1 ring-inset ring-red-400'
                                                    : job.sla_state === 'at_risk'
                                                      ? 'bg-amber-50 ring-1 ring-inset ring-amber-300'
                                                      : isSelected
                                                        ? 'bg-blue-50/80 ring-1 ring-inset ring-blue-300'
                                                        : 'hover:bg-slate-50'
                                            }`}
                                        >
                                            <td className="px-3 py-3 align-top">
                                                <p className="font-semibold text-gray-900 font-mono text-xs">
                                                    {job.service_number}
                                                </p>
                                                {job.sla_state === 'breached' || job.sla_state === 'at_risk' ? (
                                                    <p
                                                        className={`text-[11px] font-semibold mt-0.5 ${
                                                            job.sla_state === 'breached' ? 'text-red-700' : 'text-amber-700'
                                                        }`}
                                                    >
                                                        {job.sla_state === 'breached' ? 'SLA breached' : 'SLA at risk'}
                                                        {job.sla_phase ? ` · ${job.sla_phase}` : ''}
                                                    </p>
                                                ) : (
                                                    <p className="text-[11px] text-gray-400 mt-0.5">
                                                        {formatDateTime(job.updated_at || job.created_at)}
                                                    </p>
                                                )}
                                            </td>
                                            <td className="px-3 py-3 align-top">
                                                <p className="text-gray-800 font-medium leading-snug">
                                                    {truncate(job.customer_name, 22)}
                                                </p>
                                                <p className="text-[11px] text-gray-400 mt-0.5 xl:hidden">
                                                    {truncate(job.vendor_name, 24)}
                                                </p>
                                            </td>
                                            <td className="px-3 py-3 align-top hidden xl:table-cell">
                                                <p className="text-gray-700 text-xs leading-snug" title={job.vendor_name}>
                                                    {truncate(job.vendor_name, 32)}
                                                </p>
                                                <p className="text-[11px] text-gray-500 mt-0.5">
                                                    {job.driver_name && job.driver_name !== '—'
                                                        ? `Driver: ${job.driver_name}`
                                                        : 'No driver assigned'}
                                                </p>
                                            </td>
                                            <td className="px-3 py-3 align-top">
                                                <ServiceStatusBadge
                                                    status={job.status}
                                                    catalog={statusCatalog}
                                                    escalated={job.completion_otp_escalated}
                                                    compact
                                                />
                                            </td>
                                            <td className="px-3 py-3 align-top">
                                                <div className="flex justify-end gap-1">
                                                    <button
                                                        type="button"
                                                        title="Show on map"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onSelectJob?.(job);
                                                        }}
                                                        className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-white hover:text-blue-600 hover:border-blue-200"
                                                    >
                                                        <MapPin className="w-3.5 h-3.5" />
                                                    </button>
                                                    {trackable ? (
                                                        <button
                                                            type="button"
                                                            title="Track live"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onTrackJob?.(job);
                                                            }}
                                                            className="p-1.5 rounded-md border border-teal-200 text-teal-700 bg-teal-50 hover:bg-teal-100"
                                                        >
                                                            <Navigation className="w-3.5 h-3.5" />
                                                        </button>
                                                    ) : null}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <p className="text-xs text-gray-400 mt-2 px-1">
                Showing <span className="font-medium text-gray-600">{filtered.length}</span> of{' '}
                <span className="font-medium text-gray-600">{jobs.length}</span> active jobs
                {jobs.length >= 100 ? ' (capped at 100)' : ''}
            </p>
        </div>
    );
};

export default LiveJobsList;
